import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Collapse,
  Paper,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useExplorerDates } from "./Explorer/useExplorerDates";
import { useAuth } from "../contexts/AuthContext";
import DayRangePicker from "../components/DayRangePicker";
import ExplorerExportButton from "./Explorer/ExplorerExportButton";
import type { ExportConfig } from "../export";
import { entityExportReports, exportAlternatives } from "./Explorer/explorerTabs";
import {
  TrendChartCard,
  autoTrendGranularity,
  GRANULARITY_LABELS,
} from "./Explorer/TrendChartCard";
import { SummaryCard } from "./Explorer/SummarySection";
import { PlacementStrip } from "./Explorer/PlacementBadge";
import { MentionEpisodeRows } from "./Explorer/ExplorerBrandsSection";
import { ProgramDetailBrandRowSkeleton, EXPLORER_PANEL_CONTENT_SX, EXPLORER_PANEL_LIST_SX } from "./Explorer/ExplorerLoadingSkeletons";
import {
  calculateMedian,
  explorerAccentAt,
  STALE_TIME,
  GC_TIME,
  brandCategoryLabel,
} from "./Explorer/utils";
import { ExplorerHeader } from "./Explorer/ExplorerHeader";
import apiService from "../services/api";
import { EntityImage } from "../components/EntityImage";
import {
  getS3Url,
  ensureDirectS3Url,
  resolveProgramCoverUrl,
} from "../utils/imageUtils";
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const ExplorerBrandDetail: React.FC = () => {
  const { t } = useTranslation("explorer");
const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();

  const { getDateFilterMinStartDate, getDateFilterMaxEndDate, hasExplorerTabAccess } = useAuth();
  const { startDate, endDate, setRange } = useExplorerDates();
  const currentMinDate = getDateFilterMinStartDate();
  const currentMaxDate = getDateFilterMaxEndDate();
  const trendGranularity = useMemo(
    () => autoTrendGranularity(startDate, endDate),
    [startDate, endDate],
  );
  const trendLabels = GRANULARITY_LABELS[trendGranularity];

  const [expandedProgramId, setExpandedProgramId] = useState<string | null>(
    null,
  );

  const { data, isLoading, isFetching, isPlaceholderData, error } = useQuery({
    queryKey: ["explorer-brand-detail", brandId, startDate, endDate],
    queryFn: () =>
      apiService.getExplorerBrandDetail(brandId!, startDate, endDate),
    enabled: !!brandId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    placeholderData: keepPreviousData,
  });

  const { data: trendsData, isFetching: trendsFetching, isPlaceholderData: trendsIsPlaceholderData } = useQuery({
    queryKey: ["explorer-brand-detail-trends", brandId, startDate, endDate, trendGranularity],
    queryFn: () =>
      apiService.getExplorerBrandDetailTrends(
        brandId!,
        startDate,
        endDate,
        trendGranularity,
      ),
    enabled: !!brandId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    placeholderData: keepPreviousData,
  });

  const { data: programMentionsData, isLoading: programMentionsLoading } =
    useQuery({
      queryKey: [
        "explorer-program-brand-mentions",
        expandedProgramId,
        brandId,
        startDate,
        endDate,
        "all",
      ],
      queryFn: () =>
        apiService.getExplorerProgramBrandMentions(
          expandedProgramId!,
          brandId!,
          startDate,
          endDate,
          "all",
        ),
      enabled: !!expandedProgramId && !!brandId,
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
    });

  const handleProgramClick = (programId: string) => {
    setExpandedProgramId(expandedProgramId === programId ? null : programId);
  };

  const exportReports = entityExportReports(hasExplorerTabAccess, "brand").join(",");
  const exportBrandName = data?.brand?.name;
  const exportConfig = useMemo<ExportConfig | undefined>(() => {
    const reports = exportReports ? exportReports.split(",") : [];
    if (!brandId || reports.length === 0) return undefined;
    return {
      reportType: reports[0]!,
      params: { startDate, endDate },
      contextLabels: exportBrandName ? [{ label: t("brand"), value: exportBrandName }] : [],
      initialFilters: { brandIds: [brandId] },
      alternatives: exportAlternatives(reports),
    };
  }, [brandId, exportBrandName, startDate, endDate, exportReports, t]);

  const showDetailSkeleton =
    (isLoading && !data) || (isFetching && isPlaceholderData);
  const showTrendsSkeleton =
    trendsFetching && (!trendsData || trendsIsPlaceholderData);

  if (error && !data) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Alert severity="error">{t("loadBrandError")}</Alert>
      </Container>
    );
  }

  const brand = data?.brand;
  const programs = data?.programs ?? [];
  const totalMentions = data?.totalMentions ?? 0;
  const totalPrograms = data?.totalPrograms ?? 0;
  const totalViews = data?.totalViews ?? 0;

  const trends = trendsData?.trends ?? [];
  const hasMentionData = trends.some(
    (t) => (t.mentionsContent ?? t.mentions ?? 0) > 0,
  );

  return (
    <Container maxWidth="xl" sx={{ pb: 4 }}>
      <ExplorerHeader
        loading={showDetailSkeleton}
        onBack={() => navigate(-1)}
        image={{
          name: brand?.name ?? "",
          src: brand
            ? brand.s3Key
              ? ensureDirectS3Url(getS3Url(brand.s3Key))
              : brand.logoUrl
            : undefined,
        }}
        title={brand?.name ?? ""}
        subtitle={
          brand?.category
            ? brandCategoryLabel(brand.category) || brand.category
            : ""
        }
      >
        <DayRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={setRange}
          minDate={currentMinDate}
          maxDate={currentMaxDate}
        />
        {exportConfig ? <ExplorerExportButton config={exportConfig} /> : null}
      </ExplorerHeader>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          {t("periodSummary")}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="stretch"
        >
          <SummaryCard
            title={t("viewsTotal")}
            value={formatNumber(totalViews)}
            color={explorerAccentAt(0)}
            isLoading={showDetailSkeleton}
          />
          <SummaryCard
            title={t("contentMentions")}
            value={formatNumber(totalMentions)}
            color={explorerAccentAt(1)}
            isLoading={showDetailSkeleton}
          />
          <SummaryCard
            title={t("programs")}
            value={totalPrograms}
            color={explorerAccentAt(2)}
            isLoading={showDetailSkeleton}
          />
        </Stack>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          {trendLabels.title}
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TrendChartCard
            title={t("views")}
            dataKey="views"
            color={explorerAccentAt(0)}
            tooltipLabel={t("views")}
            granularityTooltip={trendLabels.tooltip}
            data={trends}
            median={
              trends.length > 0 && !showTrendsSkeleton
                ? calculateMedian(
                    trends
                      .map((t) => t.views)
                      .filter((v): v is number => typeof v === "number"),
                  )
                : undefined
            }
            formatValue={formatNumber}
            yAxisWidth={45}
            isLoading={showTrendsSkeleton}
          />
          {(showTrendsSkeleton || hasMentionData) && (
            <TrendChartCard
              title={t("contentMentions")}
              dataKey="mentionsContent"
              color={explorerAccentAt(1)}
              tooltipLabel={t("contentMentions")}
              granularityTooltip={trendLabels.tooltip}
              data={trends.map((t) => ({
                ...t,
                mentionsContent: t.mentionsContent ?? t.mentions ?? 0,
              }))}
              median={
                trends.length > 0 && !showTrendsSkeleton
                  ? calculateMedian(
                      trends
                        .map((t) => t.mentionsContent ?? t.mentions ?? 0)
                        .filter(
                          (v): v is number => typeof v === "number" && v > 0,
                        ),
                    )
                  : undefined
              }
              allowDecimals={false}
              isLoading={showTrendsSkeleton}
            />
          )}
          <TrendChartCard
            title={t("programs")}
            dataKey="programs"
            color={explorerAccentAt(
              showTrendsSkeleton || hasMentionData ? 2 : 1,
            )}
            tooltipLabel={t("programs")}
            granularityTooltip={trendLabels.tooltip}
            data={trends}
            median={
              trends.length > 0 && !showTrendsSkeleton
                ? calculateMedian(
                    trends
                      .map((t) => t.programs)
                      .filter((v): v is number => typeof v === "number"),
                  )
                : undefined
            }
            allowDecimals={false}
            isLoading={showTrendsSkeleton}
          />
        </Stack>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h2">
            {t("programsWithCount", { total: showDetailSkeleton ? "…" : programs.length })}
          </Typography>
          {isFetching && !showDetailSkeleton && <CircularProgress size={16} />}
        </Stack>

        {showDetailSkeleton ? (
          <Paper sx={EXPLORER_PANEL_CONTENT_SX}>
            {Array.from({ length: 4 }, (_, i) => (
              <ProgramDetailBrandRowSkeleton key={`program-skel-${i}`} />
            ))}
          </Paper>
        ) : programs.length === 0 ? (
          <Paper sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {t("noProgramsWithMentions")}
            </Typography>
          </Paper>
        ) : (
          <Paper>
            <List disablePadding sx={EXPLORER_PANEL_LIST_SX}>
            {programs.map((program, index) => {
              const isExpanded = expandedProgramId === program._id;
              const mentionCount =
                (program.contentMentions ?? 0) +
                (program.physicalMentions ?? 0) +
                (program.bannerMentions ?? 0) +
                (program.creditsMentions ?? 0);
              return (
                <React.Fragment key={program._id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    disablePadding
                    sx={{
                      py: 1.5,
                      px: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                      bgcolor: isExpanded ? "action.selected" : "transparent",
                    }}
                    onClick={() => handleProgramClick(program._id)}
                  >
                    <ListItemAvatar>
                      <EntityImage
                        name={program.name}
                        src={resolveProgramCoverUrl({
                          program,
                          podcast: program.podcast ?? undefined,
                        })}
                        size={48}
                        variant="plain"
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={program.name}
                      secondary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{program.episodeCount} episodios</span>
                          <PlacementStrip
                            contentMentions={program.contentMentions}
                            physicalMentions={program.physicalMentions}
                            bannerMentions={program.bannerMentions}
                            creditsMentions={program.creditsMentions}
                          />
                        </Stack>
                      }
                    />
                    <Chip
                      label={t("mentionCount", { count: mentionCount })}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                    <IconButton size="small">
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </ListItem>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ ...EXPLORER_PANEL_CONTENT_SX, pt: 1, pl: 9 }}>
                      {programMentionsLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            py: 1,
                          }}
                        >
                          <CircularProgress size={16} />
                          <Typography variant="body2" color="text.secondary">
                            Cargando menciones...
                          </Typography>
                        </Box>
                      ) : !programMentionsData?.mentions?.length ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 1 }}
                        >
                          No se encontraron menciones
                        </Typography>
                      ) : (
                        <MentionEpisodeRows
                          mentions={programMentionsData.mentions}
                          brandName={brand?.name}
                          brandS3Key={brand?.s3Key}
                        />
                      )}
                    </Box>
                  </Collapse>
                </React.Fragment>
              );
            })}
            </List>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default ExplorerBrandDetail;
